import React from 'react';
import { Helmet } from 'react-helmet';
import Footer from './../../components/Footer';

const ETFs = () => {
  const title = "ETF'ler: Borsa Yatırım Fonlarını ve Faydalarını Anlamak | Smart Finans";
  const description = "ETF'lerin ne olduğunu ve yatırımcılara nasıl fayda sağladığını öğrenin. Bu makalede, ETF'lerin nasıl çalıştığını ve yatırım portföyünüzde nasıl kullanılabileceğini keşfedin.";
  const canonicalUrl = "https://smart-portfoy.com/egitim/risk-toleransi";

  const sections = [
    {
      title: "ETF Nedir?",
      description: "Borsa Yatırım Fonları (ETF'ler), bir borsada işlem gören ve genellikle bir endeksi, sektörü, emtiayı veya diğer varlıkları izleyen yatırım fonlarıdır.",
      usageInfo: {
        importance: "ETF'ler, yatırımcılara çeşitlendirilmiş bir portföy oluşturma ve belirli piyasa segmentlerine erişim sağlama imkanı sunar.",
        usage: "ETF'leri kullanarak, düşük maliyetli ve verimli bir şekilde çeşitlendirilmiş bir yatırım portföyü oluşturabilirsiniz."
      }
    },
    {
      title: "ETF Türleri",
      description: "Farklı türde ETF'ler vardır:",
      points: [
        "Endeks ETF'leri",
        "Sektör ETF'leri",
        "Emtia ETF'leri",
        "Tahvil ETF'leri",
        "Uluslararası ETF'ler"
      ]
    },
    {
      title: "ETF'lerin Faydaları",
      description: "ETF'lerin yatırımcılara sağladığı bazı faydalar şunlardır:",
      points: [
        "Çeşitlendirme",
        "Düşük Maliyetler",
        "Likidite",
        "Şeffaflık",
        "Vergi Verimliliği"
      ]
    },
    {
      title: "ETF'lerin Riskleri",
      description: "ETF'lerin bazı riskleri şunlardır:",
      points: [
        "Piyasa Riski",
        "Likidite Riski",
        "İzleme Hatası",
        "Vergi Etkileri"
      ]
    },
    {
      title: "ETF'leri Yatırım Portföyünüzde Kullanma",
      description: "ETF'leri yatırım portföyünüzde kullanmanın bazı yolları:",
      points: [
        "Çeşitlendirilmiş bir temel portföy oluşturma",
        "Belirli sektörlere veya varlıklara maruz kalma",
        "Kısa vadeli ticaret stratejileri"
      ]
    },
    {
      title: "Smart Portfoy ile ETF'leri Takip Edin",
      description: "Smart Portfoy ile ETF'leri ve diğer yatırım araçlarını kolayca takip edin, otomatik raporlar oluşturun ve yatırım kararlarınızı daha bilinçli bir şekilde alın.",
      points: []
    }
  ];

  return (
    <>
<Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonicalUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <link rel="canonical" href={canonicalUrl} />
        <meta name="keywords" content="ETF, borsa yatırım fonları, yatırım, finansal analiz, çeşitlendirme" />
      </Helmet>

      <div className="min-h-screen bg-gray-800 text-gray-100">
        {/* Hero Section */}
        <header className="bg-gradient-to-r from-blue-900 to-indigo-900 py-16">
          <div className="container mx-auto px-4 max-w-6xl">
            <h1 className="text-4xl font-bold mb-4 mt-8 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-indigo-400">
              {title}
            </h1>
            <p className="text-lg text-gray-300 max-w-3xl leading-relaxed">
              {description}
            </p>
          </div>
        </header>

        {/* Image Section with Gradient Overlay */}
        <div className="relative h-48">
          <div className="absolute inset-0 bg-gradient-to-b from-gray-900/50 to-gray-900 z-10"></div>
          <img 
               src="/images/blog/interest-rates/interest-rates-guide.jpeg" 
            alt="ETF'ler İllüstrasyonu"
            className="w-full h-full object-cover"
          />
        </div>

        {/* Content Section */}
        <div className="container mx-auto px-4 max-w-6xl py-8">
          <div className="space-y-12">
            {sections.map((section, index) => (
              <section key={index} className="space-y-6 p-6 bg-gray-900/50 rounded-lg hover:bg-blue-900/50 transition duration-300 ease-in-out">
                <h2 className="text-2xl font-bold text-blue-400">{section.title}</h2>
                <p className="text-gray-300">{section.description}</p>
                
                {section.usageInfo && (
                  <div className="space-y-4 mt-4">
                    <div className="bg-gray-800/50 p-4 rounded-lg">
                      <h3 className="text-lg font-semibold text-blue-300 mb-2">Önemi</h3>
                      <p className="text-gray-300">{section.usageInfo.importance}</p>
                    </div>
                    
                    <div className="bg-gray-800/50 p-4 rounded-lg">
                      <h3 className="text-lg font-semibold text-blue-300 mb-2">Nasıl Kullanılır</h3>
                      <p className="text-gray-300">{section.usageInfo.usage}</p>
                    </div>
                  </div>
                )}

                {section.points && section.points.length > 0 && (
                  <ul className="space-y-2">
                    {section.points.map((point, pIndex) => (
                      <li key={pIndex} className="flex items-start space-x-2">
                        <span className="flex-shrink-0 w-6 h-6 bg-indigo-500 rounded-full flex items-center justify-center text-gray-900 font-bold">
                          {pIndex + 1}
                        </span>
                        <span className="text-gray-300">{point}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            ))}
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
};

export default ETFs;